import type { Dispatch } from '@reduxjs/toolkit';
import type { NavigateFunction } from 'react-router-dom';
import type { UIPaymentGroups, UIPayoutGroups } from 'store/formatters';
import type {
  Limits,
  MerchantUserFeeResponce,
  PayGroupCategories,
  PayGroupCategory,
  PayHubCashierContractsViewDataResponse,
  PayHubDomainSessionsEnumsPlatformType,
} from 'store/models';
import { PayHubDomainCashierEnumsCashierViewDataType } from 'store/models';
import { getLinkTargetType, isContentPageUrl, isMobile, transformContentUrl } from 'utils';
import type { LINK_TARGET } from 'utils/constants';

import type { FlatStringObject } from '../commonTypes';
import { setContentPageUrl } from '../store/slices/contentPage';
import { getMerchantUserFeeTranslation } from './fee';
import { isMethodAvailable } from './getIsMethodActive';
import { normalizedStringValue } from './math/math';

// ============= TYPES =============

interface NavigateForInfoProps {
  isContentPage: boolean;
  isSelfTarget: boolean;
  dispatch: Dispatch;
  linkTarget: LINK_TARGET;
  descriptionUrl: string;
  navigate: NavigateFunction;
  location: Location;
}

export interface SelectInfoMethodProps {
  isSelfTarget: boolean;
  descriptionUrl?: string;
  dispatch: Dispatch;
  platform?: PayHubDomainSessionsEnumsPlatformType;
  navigate: NavigateFunction;
  location: Location;
}

export interface SelectActionDepositMethodProps {
  method: UIPaymentGroups;
  navigate: NavigateFunction;
  dispatch: Dispatch;
  flowType: string;
}

export interface SelectActionWithdrawalMethodProps {
  method: UIPayoutGroups;
  navigate: NavigateFunction;
  dispatch: Dispatch;
  flowType: string;
}

export enum GROUPS {
  Disabled,
  Active,
}

export interface GroupList<T extends UIPaymentGroups | UIPayoutGroups> {
  name: GROUPS;
  list: T[];
}

type TFunction = (t: string, params?: FlatStringObject) => string;

interface FormMethodSubTitleProps {
  t: TFunction;
  processingTime?: string;
  fee: MerchantUserFeeResponce | string;
  limits: Limits;
  currency?: string;
}

// ============= CATEGORY TYPES (Shared) =============

export interface CategoryConfig {
  isStrict: boolean;
  useOnPW: boolean;
  hasCategories: boolean;
  categories: PayGroupCategory[];
}

export interface GroupedMethodsByCategory {
  categoryName: string;
  arrangement: number;
  methods: UIPaymentGroups[];
}

export interface GroupedMethodsWithdrawalByCategory {
  categoryName: string;
  arrangement: number;
  methods: UIPayoutGroups[];
}

export const DISABLED_CATEGORY_KEY = 'PH.DISABLED_METHOD_LIST_CAPTION';
export const REGULAR_CATEGORY_KEY = 'PH.REGULAR_METHOD_LIST_CAPTION';
export const ALL_METHODS_CATEGORY_KEY = 'PH.BUTTON.RETURN_TO_ALL_PAY_METHODS';

// ============= GENERAL UTILITIES =============

export const getTwoMethodColumns = <T>(methods: T[]) => {
  if (!methods?.length) {
    return [];
  }

  return methods.reduce(
    (acc: [T[], T[]], current, index) => {
      acc[index % 2].push(current);
      return acc;
    },
    [[], []],
  );
};

export const navigateForInfo = (props: NavigateForInfoProps): void => {
  const { isContentPage, isSelfTarget, linkTarget, dispatch, descriptionUrl, navigate, location } = props;

  const navigateMap = {
    _self: (): void =>
      navigate('/content', {
        state: {
          from: location.pathname,
          background: location,
        },
      }),
    _blank: (): void => {
      const newWindow = window.open(descriptionUrl, '_blank', 'noopener,noreferrer');

      if (!newWindow && isMobile) {
        window.location.href = descriptionUrl;
      }
    },
  };

  if (isContentPage && isSelfTarget) {
    dispatch(setContentPageUrl(descriptionUrl));
  }

  navigateMap[linkTarget]();
};

export const selectInfoMethod = (props: SelectInfoMethodProps): void => {
  const { descriptionUrl, isSelfTarget, dispatch, platform, navigate, location } = props;

  if (!descriptionUrl) {
    return;
  }

  const transformedUrl = transformContentUrl(descriptionUrl);
  const isContentPage = isContentPageUrl(transformedUrl);
  const linkTarget = getLinkTargetType({
    platform,
    isSelfTarget,
  });

  navigateForInfo({
    isContentPage,
    isSelfTarget,
    dispatch,
    linkTarget,
    descriptionUrl: transformedUrl,
    navigate,
    location,
  });
};

export const formMethodSubTitle = (props: FormMethodSubTitleProps): string => {
  const { t, processingTime = '', fee, limits, currency = '' } = props;

  const separator = ' • ';
  const feeText = getMerchantUserFeeTranslation(fee, t);
  const processingTimeText = t(processingTime) || '';

  const minLimit = limits?.min || 0;
  const limitText =
    limits.displayLimits && minLimit > 0
      ? t('PH.MIN_LIMIT_INFO_TEXT', {
          amount: normalizedStringValue(`${minLimit}`),
          currency,
        })
      : '';

  const subTitleArr = [limitText, processingTimeText, feeText];

  return subTitleArr.filter((element) => Boolean(element)).join(separator);
};

export const getMethodGroups = <T extends UIPaymentGroups | UIPayoutGroups>(methods: T[]): GroupList<T>[] => {
  if (!methods.length) {
    return [];
  }

  const availableMethods: T[] = [];
  const disabledMethods: T[] = [];

  methods.forEach((method) => {
    if (!isMethodAvailable(method)) {
      disabledMethods.push(method);
    } else {
      availableMethods.push(method);
    }
  });

  const result: GroupList<T>[] = [];

  if (availableMethods.length > 0) {
    result.push({
      name: GROUPS.Active,
      list: availableMethods,
    });
  }

  if (disabledMethods.length > 0) {
    result.push({
      name: GROUPS.Disabled,
      list: disabledMethods,
    });
  }

  return result;
};

export const getGroupsCount = (methodGroups: GroupList<UIPaymentGroups | UIPayoutGroups>[]) => {
  const notEmptyGroups = methodGroups.filter((group) => group.list && group.list.length > 0);
  return notEmptyGroups.length;
};

/**
 * Get disabled methods that belong to a specific category
 */
export const getDisabledMethodsForCategory = <T extends UIPaymentGroups | UIPayoutGroups>(
  categoryName: string,
  categories: PayGroupCategory[],
  disabledMethods: T[],
): T[] => {
  const category = categories.find((cat) => cat.category_name === categoryName);

  if (!category) {
    return [];
  }

  return disabledMethods.filter((method) => category.pay_group_ids.includes(method.id));
};

export const getRestrictedUntilText = (t: TFunction, restrictedUntil?: string) => {
  if (!restrictedUntil) {
    return {
      key: '',
      message: '',
    };
  }

  const now = new Date().getTime();
  const restrictedTime = new Date(restrictedUntil);
  const dif = restrictedTime.getTime() - now;
  const DD = restrictedTime.getDate().toString().padStart(2, '0');
  const MM = (restrictedTime.getMonth() + 1).toString().padStart(2, '0');
  const hh = restrictedTime.getHours().toString().padStart(2, '0');
  const mm = restrictedTime.getMinutes().toString().padStart(2, '0');
  const mmDif = Math.floor((dif % 3600000) / 60000)
    .toString()
    .padStart(2, '0');
  let resctrictedKey = '';
  let time = '';
  const result = {
    key: '',
    message: '',
  };

  if (dif > 3600000 * 12) {
    resctrictedKey = 'CASHIER.RESTRICT_UNTIL_TEXT_BIG';
    time = `${DD}.${MM} ${hh}:${mm}`;
  } else if (dif < 3600000 * 12 && dif > 3600000) {
    resctrictedKey = 'CASHIER.RESTRICT_UNTIL_TEXT_MID';
    time = `${hh}:${mm}`;
  } else if (dif < 3600000) {
    resctrictedKey = 'CASHIER.RESTRICT_UNTIL_TEXT_SMALL';
    time = `${mmDif}`;
  }

  result.key = resctrictedKey;
  result.message = t(resctrictedKey, {
    time,
  });

  return result;
};

export const getViewData = (data?: PayHubCashierContractsViewDataResponse[] | null) => {
  if (!data?.length) {
    return {};
  }

  const additionalViewData = data?.filter(
    (obj) => obj.type === PayHubDomainCashierEnumsCashierViewDataType.AdditionalInfo,
  );
  const viewData = data?.filter((obj) => obj.type !== PayHubDomainCashierEnumsCashierViewDataType.AdditionalInfo);

  return {
    additionalViewData,
    viewData,
  };
};

// ============= CATEGORY UTILITIES (Shared between Deposit and Withdrawal) =============

/**
 * Pure utility: Parse category configuration from API response
 * Shared between Deposit and Withdrawal
 */
export const parseCategoryConfig = (paygroupCategories?: PayGroupCategories, shouldSkip?: boolean): CategoryConfig => {
  const hasCategories = Boolean(
    !shouldSkip && paygroupCategories?.categories && paygroupCategories.categories.length > 0,
  );

  return {
    isStrict: paygroupCategories?.is_category_scroll_locked || false,
    useOnPW: (!shouldSkip && paygroupCategories?.use_on_pw) || false,
    hasCategories,
    categories: shouldSkip ? [] : paygroupCategories?.categories || [],
  };
};

export const getCarouselReadyCondition = (
  isShowAllMethods: boolean,
  hasCarouselWithTabs: boolean,
  isGroupCategoryCarouselReady: boolean,
  selectedCategory: string | null,
): boolean => {
  if (isShowAllMethods) return isGroupCategoryCarouselReady;
  if (hasCarouselWithTabs) return !!selectedCategory;
  return true;
};
